import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import { CommonDialog } from '@ifca-root/react-component/src/components/Dialog/CommonDialog'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import { TextField } from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { AccCodeDropdownFullWidth } from 'components/Dropdown/AccCodeDropdown'
import {
  useGetCompanyNameQuery,
  useGetCostCentreQuery,
  useGetFaLocationQuery,
} from 'generated/graphql'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { CommonYupValidation } from 'helpers/YupSchema/yup'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { uuid } from 'uuidv4'
import * as yup from 'yup'

interface DepartmentApportionmentProps {
  department_id: string
  location_id: string
  new_percentage: number
}

interface DepartmentAllocation {
  department_id: string
  department_name: string
  department_code: string
  location_id: string
  location_name: string
  old_percentage: number
  new_percentage: number
  old_quantity: number
  new_quantity: number
  source_document_id: string
  source_document_type: string
}

interface DepartmentApportionmentDialogProps {
  open: boolean
  mode: 'add' | 'edit'
  editIndex?: number
  editData?: DepartmentAllocation
  allocations: DepartmentAllocation[]
  totalQuantity: number
  CompanyID: string
  onClose: () => void
  onSave: (
    allocation: DepartmentAllocation,
    mode: 'add' | 'edit',
    index?: number
  ) => void
  onSaveAndNew: (allocation: DepartmentAllocation) => void
  disabled?: boolean
}

export const DepartmentApportionmentDialog: React.FC<DepartmentApportionmentDialogProps> = ({
  open,
  mode,
  editIndex,
  editData,
  allocations,
  totalQuantity,
  CompanyID,
  onClose,
  onSave,
  onSaveAndNew,
  disabled = false,
}) => {
  /* -------------------------------------------- */
  /*                     STATE                    */
  /* -------------------------------------------- */
  const [errorDia, setErrorDia] = useState<boolean>(false)
  const [errMsg, setErrMsg] = useState<string>('')
  const [percentage, setPercentage] = useState<number>(
    editData?.new_percentage ?? 0
  )

  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const DepartmentApportionmentSchema = yup.object().shape({
    department_id: CommonYupValidation.requireField(SystemMsgs.department()),
    new_percentage: yup
      .number()
      .transform((value, originalValue) => {
        if (typeof originalValue === 'string') {
          const cleanedString = originalValue.replace(/,/g, '')
          if (cleanedString === '') {
            return undefined
          }
          return parseFloat(cleanedString)
        }
        return value
      })
      .min(0, 'Percentage cannot be negative')
      .max(100, 'Percentage cannot exceed 100%')
      .typeError('Percentage is required')
      .required('Percentage is required'),
  })

  const {
    handleSubmit,
    register,
    setValue,
    control,
    errors,
    reset,
    watch,
    getValues,
    clearErrors,
  } = useForm<DepartmentApportionmentProps>({
    defaultValues: {
      department_id: editData?.department_id ?? '',
      location_id: editData?.location_id ?? '',
      new_percentage: editData?.new_percentage ?? 0,
    },
    mode: 'onSubmit',
    resolver: yupResolver(DepartmentApportionmentSchema),
  })

  /* -------------------------------------------- */
  /*                   USE EFFECT                 */
  /* -------------------------------------------- */
  useEffect(() => {
    if (mode === 'edit' && editData && open) {
      setValue('department_id', editData.department_id)
      setValue('location_id', editData.location_id || '')
      setValue('new_percentage', editData.new_percentage)
      setPercentage(editData.new_percentage)
    } else if (mode === 'add' && open) {
      reset({
        department_id: '',
        location_id: '',
        new_percentage: 0,
      })
      setPercentage(0)
    }
  }, [mode, editData, open, setValue, reset])

  /* -------------------------------------------- */
  /*                     QUERY                    */
  /* -------------------------------------------- */
  const {
    loading: CompanyLoading,
    data: { getCompany } = { getCompany: [] },
  } = useGetCompanyNameQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
  })

  const {
    loading: CostCentreLoading,
    data: { getCostCentre } = { getCostCentre: [] },
  } = useGetCostCentreQuery({
    fetchPolicy: 'network-only',
    variables: {
      CompanyID,
      IsLastNode: true,
    },
  })

  const {
    loading: FALocationLoading,
    data: { getFALocation } = { getFALocation: [] },
  } = useGetFaLocationQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
  })

  /* -------------------------------------------- */
  /*                   FUNCTION                   */
  /* -------------------------------------------- */
  const handlePercentageChange = (values: any) => {
    const val = values.floatValue ?? 0
    setPercentage(Math.min(100, Math.max(0, val)))
  }

  const calculateQuantity = (pct: number): number => {
    return (pct / 100) * totalQuantity
  }

  const validateUniqueAllocation = (
    departmentId: string,
    locationId: string
  ): { isValid: boolean; message: string } => {
    const existingIndex = allocations.findIndex(
      (alloc, idx) =>
        alloc.department_id === departmentId &&
        (alloc.location_id || '') === (locationId || '') &&
        (mode === 'edit' ? idx !== editIndex : true)
    )

    if (existingIndex >= 0) {
      return {
        isValid: false,
        message:
          'This Department and Location combination already exists. Each combination must be unique.',
      }
    }

    return { isValid: true, message: '' }
  }

  const getCostCentreDetails = (costCentreId: string) => {
    return getCostCentre?.find(cc => cc?.CostCentreID === costCentreId)
  }

  const getLocationDetails = (locationId: string) => {
    return getFALocation?.find(loc => loc?.fa_location_id === locationId)
  }

  const createAllocationEntry = (
    data: DepartmentApportionmentProps
  ): DepartmentAllocation => {
    const costCentre = getCostCentreDetails(data.department_id)
    const location = getLocationDetails(data.location_id)

    const newPercentage = parseFloat(String(percentage)) || 0
    const newQuantity = calculateQuantity(newPercentage)

    return {
      department_id: data.department_id,
      department_name: costCentre?.Name || '',
      department_code: costCentre?.Code || '',
      location_id: data.location_id || '',
      location_name: location?.name || '',
      old_percentage: mode === 'edit' ? editData?.old_percentage ?? 0 : 0,
      new_percentage: newPercentage,
      old_quantity: mode === 'edit' ? editData?.old_quantity ?? 0 : 0,
      new_quantity: newQuantity,
      source_document_id:
        mode === 'edit' ? editData?.source_document_id || uuid() : uuid(),
      source_document_type:
        mode === 'edit' ? editData?.source_document_type || 'MANUAL' : 'MANUAL',
    }
  }

  const onSubmit = (
    data: DepartmentApportionmentProps,
    saveMode: 'save' | 'new'
  ) => {
    const validation = validateUniqueAllocation(
      data.department_id,
      data.location_id
    )
    if (!validation.isValid) {
      setErrMsg(validation.message)
      setErrorDia(true)
      return
    }

    const allocation = createAllocationEntry(data)

    if (saveMode === 'new') {
      onSaveAndNew(allocation)
      reset({
        department_id: '',
        location_id: '',
        new_percentage: 0,
      })
      setPercentage(0)
    } else {
      onSave(allocation, mode, editIndex)
    }
  }

  const isLoading = CompanyLoading || CostCentreLoading || FALocationLoading

  return (
    <>
      {isLoading && <Loading />}

      <CommonDialog
        fullWidth={true}
        open={open}
        onClose={onClose}
        sections={{
          header: {
            title: 'Department Apportionment',
            rightText: mode === 'add' ? 'New' : 'Edit',
          },
          body: () => (
            <>
              <CardContents>
                {!CostCentreLoading && (
                  <Controller
                    render={({ value, onChange }) => {
                      const selectedCC = getCostCentre
                        ?.sort((a, b) => a.Code.localeCompare(b.Code))
                        ?.find(cc => cc?.CostCentreID === value)

                      return (
                        <Autocomplete
                          options={getCostCentre || []}
                          value={selectedCC || null}
                          getOptionLabel={option =>
                            option ? `${option?.Code} | ${option?.Name}` : ''
                          }
                          fullWidth
                          onChange={(event, newValue: any) => {
                            onChange(newValue?.CostCentreID || '')
                            clearErrors('department_id')
                          }}
                          disabled={disabled}
                          PopperComponent={AccCodeDropdownFullWidth}
                          renderOption={option => (
                            <div>
                              <div>
                                <span className="xsTitle">{option?.Code}</span>
                              </div>
                              <div className="desc">{option?.Name}</div>
                            </div>
                          )}
                          renderInput={(params: any) => (
                            <TextField
                              {...params}
                              label="Department"
                              InputLabelProps={
                                selectedCC ? { shrink: true } : undefined
                              }
                              style={{ width: '100%' }}
                              margin="normal"
                              helperText={errors?.department_id?.message}
                              error={!!errors?.department_id}
                              required
                              disabled={disabled}
                            />
                          )}
                        />
                      )
                    }}
                    name="department_id"
                    control={control}
                    ref={register}
                    defaultValue={editData?.department_id || ''}
                  />
                )}

                {!FALocationLoading && (
                  <Controller
                    render={({ value, onChange }) => {
                      const selectedLocation = getFALocation
                        ?.sort((a, b) => a.name.localeCompare(b.name))
                        ?.find(loc => loc?.fa_location_id === value)

                      return (
                        <Autocomplete
                          options={
                            getFALocation?.sort((a, b) =>
                              a.name.localeCompare(b.name)
                            ) || []
                          }
                          value={selectedLocation || null}
                          getOptionLabel={option =>
                            option
                              ? `${option.name}${
                                  option.description
                                    ? ' | ' + option.description
                                    : ''
                                }`
                              : ''
                          }
                          fullWidth
                          onChange={(event, newValue) => {
                            onChange(newValue?.fa_location_id || '')
                          }}
                          disabled={disabled}
                          PopperComponent={AccCodeDropdownFullWidth}
                          renderOption={option => (
                            <div>
                              <div>
                                <span className="xsTitle">{option?.name}</span>
                                {option?.description && (
                                  <span className="xsDescription">
                                    {' '}
                                    - {option.description}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                          renderInput={params => (
                            <TextField
                              {...params}
                              label="Location"
                              InputLabelProps={
                                selectedLocation ? { shrink: true } : undefined
                              }
                              margin="normal"
                              helperText={errors?.location_id?.message}
                              error={!!errors?.location_id}
                            />
                          )}
                        />
                      )
                    }}
                    name="location_id"
                    control={control}
                    ref={register}
                    defaultValue={editData?.location_id || ''}
                  />
                )}

                {/* Percentage Input */}
                <Controller
                  as={<NumberFormat allowNegative={false} />}
                  customInput={TextField}
                  name="new_percentage"
                  label="Apportionment (%)"
                  control={control}
                  ref={register}
                  value={percentage}
                  decimalScale={2}
                  onValueChange={handlePercentageChange}
                  thousandSeparator
                  fixedDecimalScale
                  margin="normal"
                  fullWidth
                  helperText={errors?.new_percentage?.message}
                  error={!!errors?.new_percentage}
                  required
                  disabled={disabled}
                  InputProps={{
                    inputProps: { min: 0, max: 100 },
                  }}
                />

                {/* Calculated Quantity Display */}
                <TextField
                  label="Calculated Quantity"
                  value={calculateQuantity(percentage).toFixed(2)}
                  fullWidth
                  margin="dense"
                  InputProps={{
                    disableUnderline: true,
                    readOnly: true,
                  }}
                  disabled
                />
              </CardContents>

              <ErrorDialog
                errorDia={errorDia}
                setErrorDia={setErrorDia}
                errorMsg={errMsg}
                errorHeaderMsg={'Validation Error'}
              />
            </>
          ),
          footer: {
            actions:
              mode === 'add'
                ? [
                    {
                      displayText: 'Close',
                      props: {
                        onClick: onClose,
                        variant: 'contained',
                        color: 'primary',
                      },
                    },
                    {
                      displayText: 'Confirm & New',
                      props: {
                        onClick: () => {
                          handleSubmit(data => onSubmit(data, 'new'))()
                        },
                        variant: 'contained',
                        color: 'primary',
                        disabled: disabled,
                      },
                    },
                    {
                      displayText: 'Confirm',
                      props: {
                        onClick: () => {
                          handleSubmit(data => onSubmit(data, 'save'))()
                        },
                        variant: 'contained',
                        color: 'primary',
                        disabled: disabled,
                      },
                    },
                  ]
                : [
                    {
                      displayText: 'Close',
                      props: {
                        onClick: onClose,
                        variant: 'contained',
                        color: 'primary',
                      },
                    },
                    {
                      displayText: 'Confirm',
                      props: {
                        onClick: () => {
                          handleSubmit(data => onSubmit(data, 'save'))()
                        },
                        variant: 'contained',
                        color: 'primary',
                        disabled: disabled,
                      },
                    },
                  ],
          },
        }}
      />
    </>
  )
}
